import { ChangeDetectionStrategy, Component, inject, OnInit } from '@angular/core';
import { MpSelfEnrolmentFormService } from '../mp-self-enrolment-form-service';
import { MpSelfEnrolmentNavService } from '../mp-self-enrolment-nav-service';
import { TypeService } from '../../../../../../services/type/type-service';
import { map, switchMap, take, tap } from 'rxjs/operators';
import { DropDownItem } from '../../../../../../models/shared/stylesheet/drop-down-item';
import { BaseButtonSizeEnum } from '../../../../../shared/components/base-button/base-button-size.enum';
import { Subscribable } from '../../../../../../models/base/subscribable';
import { BehaviorSubject, combineLatest, forkJoin, Observable } from 'rxjs';
import { Gender } from '../../../../../../models/types/dto/gender';
import { Pronoun } from '../../../../../../models/users/dto/pronoun';
import { DateUtils } from '../../../../../../utils/date-utils';

@Component({
  selector: 'app-mp-self-enrolment-personal-information',
  templateUrl: './mp-self-enrolment-personal-information.component.html',
  styleUrls: ['./mp-self-enrolment-personal-information.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MpSelfEnrolmentPersonalInformationComponent extends Subscribable implements OnInit {
  private formService = inject(MpSelfEnrolmentFormService);
  private navService = inject(MpSelfEnrolmentNavService);
  private typeService = inject(TypeService);

  public formSubmitted = false;
  public formGroup = this.formService.personalInfoForm;
  public customErrorMessages = {
    birthDate: {
      underage: $localize`You must be at least 18 years old to join`
    },
    socialInsuranceNumber: {
      invalidSin: $localize`Social Insurance Number is not valid`
    }
  };

  public today = DateUtils.todayAsDateOnlyString();

  private _isLoading = new BehaviorSubject<boolean>(false);
  public isLoading$ = this._isLoading as Observable<boolean>;

  private _genders = new BehaviorSubject<Gender[]>([]);
  public genders$ = this._genders as Observable<Gender[]>;

  private _pronouns = new BehaviorSubject<Pronoun[]>([]);
  public pronouns$ = this._pronouns as Observable<Pronoun[]>;

  public genderDropdowns$ = this.genders$.pipe(
    map(genders => {
      return genders.map(g => new DropDownItem(g?.name, g?.id));
    })
  );

  public pronounDropdowns$ = this.pronouns$.pipe(
    map(pronouns => {
      return pronouns.map(p => new DropDownItem(p?.name, p?.id));
    })
  );

  public ngOnInit(): void {
    combineLatest([this.typeService.gendersAreLoaded$, this.typeService.pronounsAreLoaded$])
      .pipe(
        take(1),
        tap(([gendersLoaded, pronounsLoaded]) => {
          if (!gendersLoaded || !pronounsLoaded) {
            this._isLoading.next(true);
          }
        }),
        switchMap(() => forkJoin([this.typeService.genders$, this.typeService.pronouns$])),
        take(1)
      )
      .subscribe(([genders, pronouns]) => {
        this._genders.next(genders);
        this._pronouns.next(pronouns);
        this._isLoading.next(false);
      });
  }

  public submit(): void {
    this.formSubmitted = true;
    if (this.formGroup.valid) {
      this.navService.goForward();
    }
  }

  protected readonly BaseButtonSizeEnum = BaseButtonSizeEnum;
}
